import { useEffect, useRef, useState } from "react";

const POLL_INTERVAL_MS = 3000;
// About a minute — P24 usually confirms within seconds, this covers slow webhooks
const MAX_ATTEMPTS = 20;

type OrderWithPayments = {
  id: string;
  payments: Array<{ id: string; status?: string | null }>;
};

export type PaymentConfirmationState = "idle" | "waiting" | "confirmed" | "timeout";

/**
 * After returning from Przelewy24 the payment is confirmed asynchronously (P24 webhook → SNS →
 * order update), so the page has no way to learn about it on its own. While the URL points at a
 * just-paid payment, refetch the orders until that payment shows as paid or the attempts run out.
 */
export default function usePaymentConfirmation(
  orders: OrderWithPayments[] | undefined,
  orderId: string | undefined,
  paymentId: string | undefined,
  refetch: () => unknown,
): PaymentConfirmationState {
  const [attempts, setAttempts] = useState(0);
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  const isTracking = Boolean(orderId && paymentId);
  const isPaid = Boolean(
    orders?.some(
      (order) =>
        order.id === orderId &&
        order.payments.some((payment) => payment.id === paymentId && payment.status === "PAID"),
    ),
  );

  useEffect(() => {
    if (!isTracking || isPaid || attempts >= MAX_ATTEMPTS) return;
    const timer = window.setTimeout(() => {
      refetchRef.current();
      setAttempts((previous) => previous + 1);
    }, POLL_INTERVAL_MS);
    return () => window.clearTimeout(timer);
  }, [isTracking, isPaid, attempts]);

  if (!isTracking) return "idle";
  if (isPaid) return "confirmed";
  return attempts >= MAX_ATTEMPTS ? "timeout" : "waiting";
}
